/**
 * Vulnerabilities Page
 * Table view of all CVEs with filtering, search, and pagination
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheckIcon as Shield,
  MagnifyingGlassIcon as Search,
  ExclamationTriangleIcon as AlertTriangle,
  ArrowTrendingUpIcon as TrendingUp,
  FunnelIcon as Filter,
  ChevronLeftIcon,
  ChevronRightIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

interface CVE {
  id: number;
  cve_id: string;
  description: string;
  severity: string;
  cvss_score: number;
  published_date: string;
  affected_software_count?: number;
  enrichment?: {
    risk_score: number;
    urgency_level: string;
    exploit_likelihood: string;
  };
}

const ITEMS_PER_PAGE = 25;

export const Vulnerabilities: React.FC = () => {
  const navigate = useNavigate();
  const [cves, setCves] = useState<CVE[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'published' | 'severity' | 'risk'>('published');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [useAISearch, setUseAISearch] = useState(false);
  const [aiSearchResults, setAiSearchResults] = useState<CVE[]>([]);
  const [aiSearching, setAiSearching] = useState(false);

  useEffect(() => {
    loadCVEs();
  }, []);

  // Debounced AI search
  useEffect(() => {
    if (!useAISearch || !searchTerm || searchTerm.length < 3) {
      setAiSearchResults([]);
      return;
    }

    const timer = setTimeout(() => {
      performAISearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, useAISearch]);

  const loadCVEs = async () => {
    try {
      setLoading(true);
      const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';
      const response = await fetch(`${API_BASE_URL}/cves`);
      if (!response.ok) throw new Error('Failed to load CVEs');
      const data = await response.json();
      setCves(data.data || []);
    } catch (error) {
      console.error('Failed to load CVEs:', error);
    } finally {
      setLoading(false);
    }
  };

  const performAISearch = async (query: string) => {
    try {
      setAiSearching(true);
      const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';
      const response = await fetch(`${API_BASE_URL}/ai/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, context: 'search' }),
      });

      if (!response.ok) throw new Error('AI search failed');
      const data = await response.json();

      // Extract CVE IDs from AI results
      if (data.results && data.results.length > 0) {
        setAiSearchResults(data.results);
      } else {
        setAiSearchResults([]);
      }
    } catch (error) {
      console.error('AI search error:', error);
      setAiSearchResults([]);
    } finally {
      setAiSearching(false);
    }
  };

  const filteredCVEs = (useAISearch && aiSearchResults.length > 0 ? aiSearchResults : cves)
    .filter((cve) => {
      if (useAISearch && aiSearchResults.length > 0) {
        // AI search already filtered
        const matchesSeverity =
          severityFilter === 'all' || cve.severity?.toLowerCase() === severityFilter;
        return matchesSeverity;
      }

      const matchesSearch =
        cve.cve_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cve.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesSeverity =
        severityFilter === 'all' || cve.severity?.toLowerCase() === severityFilter;
      return matchesSearch && matchesSeverity;
    })
    .sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'published') {
        comparison = new Date(b.published_date).getTime() - new Date(a.published_date).getTime();
      } else if (sortBy === 'severity') {
        comparison = (b.cvss_score || 0) - (a.cvss_score || 0);
      } else if (sortBy === 'risk') {
        comparison = (b.enrichment?.risk_score || 0) - (a.enrichment?.risk_score || 0);
      }
      return sortOrder === 'asc' ? -comparison : comparison;
    });

  // Pagination
  const totalPages = Math.ceil(filteredCVEs.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedCVEs = filteredCVEs.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, severityFilter, sortBy, sortOrder]);

  const getSeverityColor = (severity: string | null) => {
    if (!severity) return 'text-gray-400';
    switch (severity.toLowerCase()) {
      case 'critical':
        return 'text-severity-critical';
      case 'high':
        return 'text-severity-high';
      case 'medium':
        return 'text-severity-medium';
      case 'low':
        return 'text-severity-low';
      default:
        return 'text-gray-400';
    }
  };

  const getSeverityBadgeColor = (severity: string | null) => {
    if (!severity) return 'bg-gray-900/30 text-gray-400 border-gray-700/50';
    switch (severity.toLowerCase()) {
      case 'critical':
        return 'bg-red-900/30 text-red-400 border-red-700/50';
      case 'high':
        return 'bg-orange-900/30 text-orange-400 border-orange-700/50';
      case 'medium':
        return 'bg-yellow-900/30 text-yellow-400 border-yellow-700/50';
      case 'low':
        return 'bg-green-900/30 text-green-400 border-green-700/50';
      default:
        return 'bg-gray-900/30 text-gray-400 border-gray-700/50';
    }
  };

  const handleSort = (column: 'published' | 'severity' | 'risk') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-8 h-8 text-cyber-blue-400" />
          <h1 className="text-3xl font-bold text-white">Vulnerabilities</h1>
        </div>
        <p className="text-gray-400">
          Security vulnerabilities tracked across your software portfolio
        </p>
      </div>

      {/* Filters and Search */}
      <div className="bg-dark-bg-secondary rounded-lg p-4 mb-6 space-y-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={useAISearch ? "AI-powered search (e.g., 'remote code execution')" : "Search CVE ID or description..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-dark-bg-primary border border-cyber-blue-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyber-blue-500"
              />
              {aiSearching && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyber-blue-400"></div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <button
                onClick={() => setUseAISearch(!useAISearch)}
                className={`flex items-center gap-2 text-sm px-3 py-1 rounded transition-colors ${
                  useAISearch
                    ? 'bg-cyber-blue-500 text-white'
                    : 'bg-dark-bg-primary text-gray-400 hover:text-white'
                }`}
              >
                <SparklesIcon className="w-4 h-4" />
                AI Search
              </button>
              {useAISearch && (
                <span className="text-xs text-gray-500">
                  Semantic search powered by Vectorize
                </span>
              )}
            </div>
          </div>

          {/* Severity Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="px-4 py-2 bg-dark-bg-primary border border-cyber-blue-500/30 rounded-lg text-white focus:outline-none focus:border-cyber-blue-500"
            >
              <option value="all">All Severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>
      </div>

      {/* CVE Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyber-blue-400"></div>
          <p className="text-gray-400 mt-4">Loading vulnerabilities...</p>
        </div>
      ) : filteredCVEs.length === 0 ? (
        <div className="bg-dark-bg-secondary rounded-lg p-12 text-center">
          <Shield className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No vulnerabilities found</p>
        </div>
      ) : (
        <>
          <div className="bg-dark-bg-secondary rounded-lg border border-gray-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-dark-bg-tertiary border-b border-gray-800">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      CVE ID
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Description
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                      onClick={() => handleSort('severity')}
                    >
                      <div className="flex items-center gap-2">
                        Severity
                        {sortBy === 'severity' && (
                          <TrendingUp
                            className={`w-4 h-4 transition-transform ${
                              sortOrder === 'asc' ? 'rotate-180' : ''
                            }`}
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-4 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                      onClick={() => handleSort('severity')}
                    >
                      CVSS
                    </th>
                    <th
                      className="px-6 py-4 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                      onClick={() => handleSort('risk')}
                    >
                      <div className="flex items-center justify-center gap-2">
                        AI Risk
                        {sortBy === 'risk' && (
                          <TrendingUp
                            className={`w-4 h-4 transition-transform ${
                              sortOrder === 'asc' ? 'rotate-180' : ''
                            }`}
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                      onClick={() => handleSort('published')}
                    >
                      <div className="flex items-center gap-2">
                        Published
                        {sortBy === 'published' && (
                          <TrendingUp
                            className={`w-4 h-4 transition-transform ${
                              sortOrder === 'asc' ? 'rotate-180' : ''
                            }`}
                          />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Affected
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {paginatedCVEs.map((cve) => (
                    <tr
                      key={cve.id}
                      onClick={() => navigate(`/vulnerability/${cve.cve_id}`)}
                      className="hover:bg-dark-bg-tertiary transition-colors cursor-pointer"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-cyber-blue-400 font-semibold hover:underline">
                            {cve.cve_id}
                          </span>
                          {cve.enrichment?.urgency_level && (
                            <AlertTriangle
                              className={`w-4 h-4 ${
                                cve.enrichment.urgency_level === 'critical'
                                  ? 'text-red-400'
                                  : cve.enrichment.urgency_level === 'high'
                                  ? 'text-orange-400'
                                  : 'text-yellow-400'
                              }`}
                              title={`${cve.enrichment.urgency_level} urgency`}
                            />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-gray-300 line-clamp-2 max-w-2xl">
                          {cve.description}
                        </p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-3 py-1 rounded border text-xs font-semibold ${getSeverityBadgeColor(
                            cve.severity
                          )}`}
                        >
                          {cve.severity ? cve.severity.toUpperCase() : 'UNKNOWN'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`font-semibold ${getSeverityColor(cve.severity)}`}>
                          {cve.cvss_score?.toFixed(1) || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {cve.enrichment?.risk_score ? (
                          <span className="text-orange-400 font-semibold">
                            {cve.enrichment.risk_score.toFixed(1)}/10
                          </span>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-400 text-sm">
                        {new Date(cve.published_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-gray-400 text-sm">
                        {cve.affected_software_count || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-400">
                Showing {startIndex + 1}-{Math.min(endIndex, filteredCVEs.length)} of{' '}
                {filteredCVEs.length} vulnerabilities
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 bg-dark-bg-secondary border border-gray-800 rounded-lg text-gray-400 hover:text-white hover:border-cyber-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === pageNum
                            ? 'bg-cyber-blue-500 text-white'
                            : 'bg-dark-bg-secondary text-gray-400 hover:text-white border border-gray-800 hover:border-cyber-blue-500'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 bg-dark-bg-secondary border border-gray-800 rounded-lg text-gray-400 hover:text-white hover:border-cyber-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Stats Summary */}
      {!loading && (
        <div className="mt-8 text-center text-gray-400 text-sm">
          {useAISearch && aiSearchResults.length > 0 && (
            <span className="text-cyber-blue-400 mr-2">
              AI Search returned {aiSearchResults.length} results
            </span>
          )}
          Total: {cves.length} vulnerabilities in database
        </div>
      )}
    </div>
  );
};
